import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Typography,
  Button,
  Tag,
  Spin,
  message,
  Space,
  Tooltip,
  Alert
} from 'antd';
import {
  FileTextOutlined,
  CalendarOutlined,
  UserOutlined,
  LinkOutlined,
  EyeOutlined,
  ExportOutlined
} from '@ant-design/icons';
import './IssuerNews.css';
import apiClient from '../../services/apiClient';

const { Title, Text, Paragraph } = Typography;

const IssuerNews = ({ issuerName = "CANARA BANK" }) => {
  const [newsData, setNewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Base URL from environment variable
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:8080';

  // Fetch news data
  const fetchNewsData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${baseUrl}/news/issuer/${encodeURIComponent(issuerName)}`);
      if (response.status < 200 || response.status >= 300) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = response.data;

      if (result.statusCode === 200 && result.data) {
        setNewsData(result.data);
        console.log('News API Response:', result);
      } else {
        console.error('Invalid news API response structure:', result);
        setNewsData({ content: [], totalElements: 0 });
      }
    } catch (error) {
      console.error('Error fetching news data:', error);
      setError("Error fetching news data");
      setNewsData({ content: [], totalElements: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNewsData();
  }, [issuerName]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch (error) {
      return dateStr;
    }
  };

  // Handle news click
  const handleNewsClick = (news) => {
    navigate(`/news-details/${encodeURIComponent(issuerName)}/${encodeURIComponent(news.id || news.title)}`, {
      state: { news, issuerName }
    });
  };

  // Handle external link
  const handleExternalLink = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  // Get truncated text
  const getTruncatedText = (text, maxLength) => {
    if (!text) return 'N/A';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Render news cards
  const renderNewsCards = () => {
    if (!newsData?.content || newsData.content.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📰</div>
          <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '0.5rem' }}>
            No News Found
          </div>
          <div style={{ fontSize: '14px' }}>
            This issuer doesn't have any news available yet.
          </div>
        </div>
      );
    }

    return (
      <div className="news-grid-container">
        {newsData.content.map((news, index) => (
          <div key={news.id || index} className="news-card-wrapper">
            <Card
              className="news-card-modern"
              hoverable
              onClick={() => handleNewsClick(news)}
            >
              {/* Card Header */}
              <div className="news-card-header">
                <div className="news-header-content">
                  <div className="news-source-section">
                    <div className="news-source">{news.source || 'Unknown Source'}</div>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="news-card-content">
                {/* News Title */}
                <div className="news-title">
                  <Tooltip title={news.title || 'N/A'}>
                    {getTruncatedText(news.title || 'N/A', 60)}
                  </Tooltip>
                </div>

                {/* News Description */}
                <div className="news-description">
                  {getTruncatedText(news.description || 'N/A', 80)}
                </div>

                {/* Key Details Grid */}
                <div className="news-details-grid">
                  <div className="detail-item">
                    <div className="detail-value">
                      {formatDate(news.publishedDate)}
                    </div>
                    <div className="detail-label">Published</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-value">
                      {getTruncatedText(news.author || 'N/A', 15)}
                    </div>
                    <div className="detail-label">Author</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-value">
                      {getTruncatedText(news.subtitle || 'N/A', 15)}
                    </div>
                    <div className="detail-label">Subtitle</div>
                  </div>
                </div>

                {/* Tags */}
                <div className="news-tags">
                  <Tag color="blue" size="small">
                    {getTruncatedText(news.source || 'N/A', 12)}
                  </Tag>
                  <Tag color="green" size="small">
                    {getTruncatedText(news.company || 'N/A', 10)}
                  </Tag>
                </div>

                {/* View Details Button */}
                <Button
                  type="primary"
                  size="small"
                  block
                  className="view-details-btn"
                  icon={<EyeOutlined />}
                >
                  View Details
                </Button>
              </div>
            </Card>
          </div>
        ))}
      </div>
    );
  };



  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <Spin size="large" />
        <div style={{ marginTop: '1rem' }}>Loading news...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📰</div>
        <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '0.5rem' }}>
          News Not Found
        </div>
        <div style={{ fontSize: '14px' }}>
          This issuer doesn't have any news available yet.
        </div>
      </div>
    );
  }

  return (
    <div className="issuer-news-section">
      <div className="news-header">
        <Title level={3} className="section-title">
          News for {issuerName}
        </Title>
      </div>

      <Card className="news-card">
        {renderNewsCards()}
      </Card>
    </div>
  );
};

export default IssuerNews;
